import type { SiteRagReport } from '../../../types/siteRag';
import { buildReportArtifact, enqueueArtifactJob, type ReportArtifactJobRequest } from '../../../services/reportArtifactJobsClient';
import { screeningSummaryEligibility } from '../../../lib/screeningSummary/eligibility';
import { createReportArtifactRequest } from '../../../lib/reportArtifacts/reportArtifactRequest';
import { authedFetch } from '../../../lib/authedFetch';
import { hashCanonicalJson } from '../../../lib/reportGeneration/reportContractHash';
import { presentationSnapshotV1Schema } from '../../../types/reportContractsV2';
import { useStore } from '../../../store';
import { SUMMARY_BUILDER_VERSION } from '../../../lib/screeningSummary/vocabulary';
import { buildReportExportFilename } from '../../../lib/reportArtifacts/reportExportFilename';

const prebuilt = new Set<string>();

export interface SummaryDownloadOptions {
  pageSize?: 'A4' | 'Letter';
  shouldSave?: () => boolean;
  companyId?: string | null;
  save?: (blob: Blob, filename: string) => void;
}

export function buildScreeningSummaryCacheKey(
  report: SiteRagReport,
  pageSize?: 'A4' | 'Letter',
  companyId?: string | null,
): string {
  const resolvedCompanyId =
    companyId !== undefined ? (companyId ?? '') : (useStore.getState()?.companyId ?? '');
  const ownerUid = resolveSummaryReportOwnerUid(report) ?? '';
  const siteId = report.siteId ?? '';
  const revId = report.reportRevision?.revisionId ?? 'norev';
  const docHash = report.reportRevision?.documentHash ?? 'nohash';
  return `${resolvedCompanyId}:${ownerUid}:${siteId}:${report.id}:${revId}:${docHash}:${SUMMARY_BUILDER_VERSION}:${pageSize ?? 'frozen'}`;
}

export function clearScreeningSummaryCache(): void {
  prebuilt.clear();
}

export function getScreeningSummaryCacheKeys(): string[] {
  return Array.from(prebuilt.values());
}

export function resolveSummaryReportOwnerUid(report: SiteRagReport): string | undefined {
  const explicit = (report as { reportOwnerUid?: unknown }).reportOwnerUid;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  if (typeof report._ownerUid === 'string' && report._ownerUid.trim()) return report._ownerUid.trim();
  const rev = report.reportRevision;
  const pathCandidate = rev?.sourcePath ?? (rev as { bodyPath?: string } | undefined)?.bodyPath ?? rev?.presentationPath ?? rev?.documentPath ?? '';
  const pathMatch = /^users\/([^/]+)\/reports\//.exec(pathCandidate);
  if (pathMatch?.[1]?.trim()) return pathMatch[1].trim();
  return undefined;
}
/** The default paper is part of the revision's frozen presentation. */
export async function resolveScreeningSummaryPageSize(report: SiteRagReport): Promise<'A4' | 'Letter'> {
  const revision = report.reportRevision;
  if (!revision) throw new Error('The saved screening revision is unavailable.');
  if (!revision.presentationPath) {
    const inlineSize = (report as { presentationSnapshot?: { pageSize?: unknown } }).presentationSnapshot?.pageSize;
    if (inlineSize === 'A4' || inlineSize === 'Letter') return inlineSize;
  }
  const response = await authedFetch(`/api/storage/load?path=${encodeURIComponent(revision.presentationPath)}`);
  if (!response.ok) throw new Error('The saved report presentation could not be loaded.');
  const raw = await response.json();
  const snapshot = presentationSnapshotV1Schema.parse(raw);
  const { presentationHash, ...content } = snapshot;
  if (presentationHash !== revision.presentationHash || hashCanonicalJson(content) !== presentationHash) throw new Error('The saved report presentation failed its content check.');
  return snapshot.pageSize;
}

/** Enqueue only; errors remain local to the summary job and never affect screening. */
export function prebuildScreeningSummary(
  report: SiteRagReport,
  pageSize?: 'A4' | 'Letter',
  companyId?: string | null,
): void {
  // Live development previews can inspect saved evidence before workers are updated.
  // Production always retains automatic prebuild; explicit downloads are independent.
  if (import.meta.env.DEV && import.meta.env.VITE_SCREENING_SUMMARY_PREBUILD === 'false') return;
  if (!screeningSummaryEligibility(report).ok || !report.reportRevision) return;
  const revision = report.reportRevision;
  const expectedCompanyId =
    companyId !== undefined ? companyId : (useStore.getState()?.companyId ?? null);
  const key = buildScreeningSummaryCacheKey(report, pageSize, expectedCompanyId);
  if (prebuilt.has(key)) return;
  prebuilt.add(key);
  void (async () => {
    const paper = pageSize ?? await resolveScreeningSummaryPageSize(report);
    if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) {
      prebuilt.delete(key);
      return;
    }
    const ownerUid = resolveSummaryReportOwnerUid(report);
    const request: ReportArtifactJobRequest = { schemaVersion: 1, sourceKind: 'report', reportId: report.id,
      reportRevisionId: revision.revisionId, format: 'pdf', pageSize: paper, view: 'screening-summary', summaryBuilderVersion: SUMMARY_BUILDER_VERSION };
    await (ownerUid
      ? enqueueArtifactJob(request, undefined, undefined, `?reportOwnerUid=${encodeURIComponent(ownerUid)}`)
      : enqueueArtifactJob(request));
  })().catch(() => { prebuilt.delete(key); });
}

export async function downloadScreeningSummary(
  report: SiteRagReport,
  pageSizeOrOptions?: 'A4' | 'Letter' | SummaryDownloadOptions,
): Promise<void> {
  const options: SummaryDownloadOptions =
    typeof pageSizeOrOptions === 'string'
      ? { pageSize: pageSizeOrOptions }
      : (pageSizeOrOptions ?? {});
  const expectedCompanyId =
    options.companyId !== undefined ? options.companyId : (useStore.getState()?.companyId ?? null);
  const expectedReportId = report.id;
  const expectedRevisionId = report.reportRevision?.revisionId;
  if (report._metadataOnly) {
    const ok = await useStore.getState().hydrateReportBody(report.id);
    if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
    if (options.shouldSave && !options.shouldSave()) return;
    const hydrated = useStore.getState().siteRag.reports.find(r => r.id === expectedReportId);
    if (!ok || !hydrated || hydrated.reportRevision?.revisionId !== expectedRevisionId) throw new Error('Open the complete saved revision before downloading its summary.');
    report = hydrated;
  }
  const paper = options.pageSize ?? await resolveScreeningSummaryPageSize(report);
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  const request = createReportArtifactRequest(report, 'pdf', paper, { view: 'screening-summary', summaryBuilderVersion: SUMMARY_BUILDER_VERSION });
  const ownerUid = resolveSummaryReportOwnerUid(report);
  const expectedFilename = buildReportExportFilename({
    siteName: report.siteName,
    reportType: report.reportType ?? 'feasibility',
    view: 'screening-summary',
    version: report.version ?? 1,
    minorVersion: report.minorVersion ?? 0,
    format: 'pdf',
  });
  const built = ownerUid ? await buildReportArtifact(request, { reportOwnerUid: ownerUid }) : await buildReportArtifact(request);
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  if (options.save) {
    options.save(built.blob, expectedFilename);
    return;
  }
  const { saveAs } = await import('file-saver');
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  saveAs(built.blob, expectedFilename);
}
